import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Alert, Chip, Select, MenuItem,
  FormGroup, FormControlLabel, Checkbox, Avatar
} from '@mui/material';
import { Users, Plus, Shield, Gamepad2 } from 'lucide-react';
import { GAME_TEMPLATES } from '../game/gameTemplates';
import AdminModal from './AdminModal';
import TeamScreen from './TeamScreen';
import { AdminPanelSkeleton, useCachedPanelCount } from './adminPanelContent';
import { API_BASE_URL } from '../utils/api';

const FEATURE_LABELS = {
  storage: 'Storage',
  telegram: 'Telegram bots',
  'ai-tools': 'AI tools',
  embeddings: 'Embeddings',
  workflows: 'Workflows',
  characters: 'Characters',
  storyflow: 'Storyflow',
  insta: 'Insta',
  'image-generation': 'Generation',
  'emb-plot_actions': 'Анализ: Сюжет',
  'emb-images': 'Анализ: Изображения',
  'emb-characters': 'Анализ: Персонажи',
  'emb-chapters': 'Анализ: Главы',
  'emb-premium_choices': 'Анализ: Премиум',
  'emb-chapter_hooks': 'Анализ: Хуки',
  'emb-escalation': 'Анализ: Эскалация'
};

const CREATE_FEATURES = ['storage', 'telegram', 'ai-tools', 'embeddings', 'workflows', 'characters', 'storyflow', 'insta', 'image-generation'];

const cardSx = {
  p: 2,
  borderRadius: '10px',
  border: '1px solid rgba(255,255,255,0.08)',
  bgcolor: 'rgba(255,255,255,0.02)',
  cursor: 'pointer',
  transition: 'all 0.12s ease',
  '&:hover': { bgcolor: 'rgba(139,92,246,0.06)', borderColor: 'rgba(139,92,246,0.3)' }
};

export default function TeamsPanel({ getToken }) {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cachedCount, setCachedCount] = useCachedPanelCount('teams:list', 4);

  const [selectedId, setSelectedId] = useState(null);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [formName, setFormName] = useState('');
  const [formGameTemplate, setFormGameTemplate] = useState('');
  const [formFeatures, setFormFeatures] = useState(['workflows']);
  const [saving, setSaving] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getToken]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const h = await authHeaders();
      const [teamsRes, usersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/teams`, { headers: h }),
        fetch(`${API_BASE_URL}/api/users`, { headers: h })
      ]);
      if (!teamsRes.ok) throw new Error('Failed to load teams');
      if (!usersRes.ok) throw new Error('Failed to load users');
      const teamsData = await teamsRes.json();
      const usersData = await usersRes.json();
      const list = teamsData.teams || [];
      setTeams(list);
      setUsers(usersData || []);
      setCachedCount(list.length);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, setCachedCount]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setFormName('');
    setFormGameTemplate('');
    setFormFeatures(['workflows']);
    setCreateOpen(true);
  };
  const closeCreate = () => { setCreateOpen(false); setSaving(false); };

  const createTeam = async () => {
    if (!formName.trim()) return;
    try {
      setSaving(true);
      const h = await authHeaders();
      const body = {
        name: formName.trim(),
        gameTemplate: formGameTemplate,
        features: formFeatures,
        members: []
      };
      const res = await fetch(`${API_BASE_URL}/api/teams`, {
        method: 'POST', headers: h, body: JSON.stringify(body)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to create');
      }
      closeCreate();
      await fetchData();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleFormFeature = (id) => {
    setFormFeatures((prev) => prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]);
  };

  const selectedTeam = selectedId ? teams.find((t) => t.id === selectedId) : null;

  // Team detail view
  if (selectedTeam) {
    return (
      <TeamScreen
        team={selectedTeam}
        users={users}
        onBack={() => setSelectedId(null)}
        onSaved={fetchData}
        getToken={getToken}
      />
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Box sx={{ color: 'secondary.light', display: 'flex', alignItems: 'center' }}>
          <Users size={22} strokeWidth={2} />
        </Box>
        <Typography variant="h5" fontWeight={600} sx={{ flex: 1 }}>Teams</Typography>
        <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={openCreate}>
          Create team
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <AdminPanelSkeleton variant="list" count={cachedCount} />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.5 }}>
          {teams.map((team) => (
            <Box
              key={team.id}
              sx={{
                ...cardSx,
                borderColor: team.isDefault ? 'rgba(139,92,246,0.35)' : 'rgba(255,255,255,0.08)'
              }}
              onClick={() => setSelectedId(team.id)}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                {team.isDefault && <Shield size={14} style={{ color: '#a78bfa', flexShrink: 0 }} />}
                <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                  {team.name}
                </Typography>
                <Avatar sx={{ width: 22, height: 22, fontSize: '0.7rem', bgcolor: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}>
                  {(team.members || []).length}
                </Avatar>
              </Box>
              {team.gameTemplate && (
                <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                  <Gamepad2 size={12} style={{ color: '#a78bfa' }} />
                  <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 500 }}>
                    {GAME_TEMPLATES.find((g) => g.id === team.gameTemplate)?.name || team.gameTemplate}
                  </Typography>
                </Box>
              )}
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {(team.features || []).slice(0, 6).map((f) => (
                  <Chip
                    key={f}
                    label={FEATURE_LABELS[f] || f}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                ))}
                {(team.features || []).length > 6 && (
                  <Chip
                    label={`+${(team.features || []).length - 6}`}
                    size="small"
                    sx={{ fontSize: '0.65rem', height: 20, bgcolor: 'rgba(255,255,255,0.05)' }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <AdminModal
        open={createOpen}
        onClose={closeCreate}
        title="Create team"
        maxWidth="sm"
        actions={
          <>
            <Button onClick={closeCreate} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={createTeam} disabled={!formName.trim() || saving}>
              {saving ? 'Creating…' : 'Create'}
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Team name</Typography>
            <TextField
              size="small"
              fullWidth
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              autoFocus
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Game template</Typography>
            <Select size="small" value={formGameTemplate} onChange={(e) => setFormGameTemplate(e.target.value)} displayEmpty>
              <MenuItem value=""><em>None</em></MenuItem>
              {GAME_TEMPLATES.map((g) => (
                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
              ))}
            </Select>
          </Box>
          <Typography variant="subtitle2" fontWeight={600}>Initial features</Typography>
          <FormGroup row>
            {CREATE_FEATURES.map((id) => (
              <FormControlLabel
                key={id}
                sx={{ mr: 1.5 }}
                control={
                  <Checkbox size="small" checked={formFeatures.includes(id)} onChange={() => toggleFormFeature(id)} />
                }
                label={<Typography variant="body2">{FEATURE_LABELS[id]}</Typography>}
              />
            ))}
          </FormGroup>
          <Typography variant="caption" color="text.secondary">
            You can configure presets, members, and advanced features after creation.
          </Typography>
        </Box>
      </AdminModal>
    </Box>
  );
}
